import { useState, type FC } from 'react'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { KTSVG } from '../../../../_metronic/helpers'
import { useManagementDashboard } from './dashboard/hooks/useManagementDashboard'
import type { ModalType, CardMeta } from './dashboard/types'
import KpiCard from './dashboard/KpiCard'
import { KpiCardSkeleton } from '../../../shared/components/LoadingSkeletons'
import {
  KpiDetailModal,
  CompaniesModal,
  SubCompaniesModal,
  VesselsModal,
  UsersModal,
  VendorsModal,
  RolesModal,
} from './dashboard/DashboardModals'

/* ──────────── KPI Card Meta ──────────── */
const kpiMeta: Record<string, CardMeta> = {
  totalCompanies: {
    description: 'Total number of registered company groups (CompanyGroupAdmin entities).',
    endpoint: 'GET /company-group-admins',
    logic: 'Count of all company group admin records.',
  },
  totalSubCompanies: {
    description: 'Total number of sub-companies (CompanyAdmin entities) across all company groups.',
    endpoint: 'GET /company-admins',
    logic: 'Count of all company admin records.',
  },
  totalVessels: {
    description: 'Total vessel count registered in the system.',
    endpoint: 'GET /vessels',
    logic: 'Count of all vessel records.',
  },
  totalUsers: {
    description: 'Total management user accounts in the system.',
    endpoint: 'GET /users/management',
    logic: 'Count of all management user records.',
  },
  activeUsers: {
    description: 'Users with enabled account status who can log in.',
    endpoint: 'GET /users/management',
    logic: 'Count of management users where enabled = true.',
  },
  totalVendors: {
    description: 'Total registered vendors in the procurement network.',
    endpoint: 'GET /vendors',
    logic: 'Count of all vendor records.',
  },
}

const ManagementDashboard: FC = () => {
  const { loading, error, failedSections, data, kpis, refresh } = useManagementDashboard()

  /* ─── Modal State ─── */
  const [activeModal, setActiveModal] = useState<ModalType>(null)

  const openModal = (modal: ModalType) => setActiveModal(modal)
  const closeModal = () => setActiveModal(null)

  /* ─── Quick Actions ─── */
  const quickActions = [
    { label: 'Companies', icon: '/media/icons/duotune/general/gen001.svg' },
    { label: 'Sub-Companies', icon: '/media/icons/duotune/general/gen002.svg' },
    { label: 'Vessels', icon: '/media/icons/duotune/general/gen034.svg' },
    { label: 'Users', icon: '/media/icons/duotune/general/gen018.svg' },
    { label: 'Vendors', icon: '/media/icons/duotune/finance/fin001.svg' },
    { label: 'Roles', icon: '/media/icons/duotune/coding/cod001.svg' },
  ]

  const nowStr = new Date().toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  }) + ', ' + new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) + ' UTC'

  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main' style={{ height: '100vh' }}>
      <ToastContainer
        position='top-center'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ top: '5rem', zIndex: 99999 }}
      />
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid' style={{ flex: 1 }}>
          <div className='container-fluid py-4' style={{ maxWidth: '1440px' }}>
            {/* ── Header ── */}
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4'>
              <div>
                <h3 className='fw-bold text-dark mb-1'>Admin Command Center</h3>
                <span className='text-muted fs-8'>Companies, vessels, users and vendor overview</span>
              </div>
              <div className='d-flex align-items-center gap-3'>
                <span className='text-muted fs-9'>Data as of {nowStr}</span>
                <button
                  className='btn btn-icon btn-sm btn-light'
                  onClick={refresh}
                  disabled={loading}
                  title='Refresh'
                >
                  <KTSVG
                    path='/media/icons/duotune/arrows/arr075.svg'
                    className={`svg-icon-2 ${loading ? 'animate-spin' : ''}`}
                  />
                </button>
              </div>
            </div>

            {/* ── Error / Notice Banner ── */}
            {error && (
              <div className='alert alert-danger d-flex align-items-center justify-content-between p-3 mb-4 rounded-3 border border-danger-subtle'>
                <div className='d-flex align-items-center gap-3'>
                  <i className='bi bi-exclamation-triangle-fill fs-3 text-danger' />
                  <div>
                    <div className='fw-bold text-danger fs-7'>Dashboard Notice</div>
                    <div className='text-dark fs-8'>{error}</div>
                  </div>
                </div>
                <button
                  className='btn btn-sm btn-outline-danger d-flex align-items-center gap-1 px-3 py-1 text-nowrap'
                  onClick={refresh}
                  disabled={loading}
                >
                  <i className={`bi bi-arrow-clockwise ${loading ? 'spinner-border spinner-border-sm' : ''}`} />
                  {loading ? 'Retrying...' : 'Retry'}
                </button>
              </div>
            )}

            {/* ── Quick Actions ── */}
            <div className='d-flex flex-wrap gap-3 mb-4 pb-3 border-bottom'>
              {quickActions.map((qa, i) => (
                <button
                  key={i}
                  className='btn btn-light d-flex align-items-center gap-2 px-3 py-2'
                  style={{ borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}
                  onClick={() => {
                    const modalMap: Record<string, ModalType> = {
                      Companies: 'companies',
                      'Sub-Companies': 'subCompanies',
                      Vessels: 'vessels',
                      Users: 'users',
                      Vendors: 'vendors',
                      Roles: 'roles',
                    }
                    openModal(modalMap[qa.label] || 'kpiDetail')
                  }}
                >
                  <KTSVG path={qa.icon} className='svg-icon-2' />
                  {qa.label}
                </button>
              ))}
            </div>

            {/* ── Dashboard Content / Loading Skeleton ── */}
            {loading ? (
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 md:gap-6 mb-5 md:mb-6'>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={`admin-kpi-sk-${i}`} className='md:col-span-1 lg:col-span-2'>
                    <KpiCardSkeleton />
                  </div>
                ))}
              </div>
            ) : (
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 md:gap-6 mb-5 md:mb-6'>
                <div className='md:col-span-1 lg:col-span-2 flex flex-col'>
                  <KpiCard
                    title='Companies'
                    value={failedSections.includes('Companies') ? '—' : kpis.totalCompanies}
                    sub={failedSections.includes('Companies') ? 'Unavailable' : `${kpis.totalCompanies} company groups`}
                    onClick={() => openModal('companies')}
                    meta={kpiMeta.totalCompanies}
                  />
                </div>
                <div className='md:col-span-1 lg:col-span-2 flex flex-col'>
                  <KpiCard
                    title='Sub-Companies'
                    value={failedSections.includes('Sub-Companies') ? '—' : kpis.totalSubCompanies}
                    sub={failedSections.includes('Sub-Companies') ? 'Unavailable' : `${kpis.totalSubCompanies} sub-companies`}
                    onClick={() => openModal('subCompanies')}
                    meta={kpiMeta.totalSubCompanies}
                  />
                </div>
                <div className='md:col-span-1 lg:col-span-2 flex flex-col'>
                  <KpiCard
                    title='Vessels'
                    value={failedSections.includes('Vessels') ? '—' : kpis.totalVessels}
                    sub={failedSections.includes('Vessels') ? 'Unavailable' : `${kpis.totalVessels} registered`}
                    onClick={() => openModal('vessels')}
                    meta={kpiMeta.totalVessels}
                  />
                </div>
                <div className='md:col-span-1 lg:col-span-2 flex flex-col'>
                  <KpiCard
                    title='Users'
                    value={failedSections.includes('Users') ? '—' : kpis.totalUsers}
                    sub={failedSections.includes('Users') ? 'Unavailable' : `${kpis.activeUsers} active`}
                    onClick={() => openModal('users')}
                    meta={kpiMeta.totalUsers}
                  />
                </div>
                <div className='md:col-span-1 lg:col-span-2 flex flex-col'>
                  <KpiCard
                    title='Active Users'
                    value={failedSections.includes('Users') ? '—' : kpis.activeUsers}
                    sub={failedSections.includes('Users') ? 'Unavailable' : `${kpis.totalUsers - kpis.activeUsers} disabled`}
                    onClick={() => openModal('users')}
                    meta={kpiMeta.activeUsers}
                  />
                </div>
                <div className='md:col-span-1 lg:col-span-2 flex flex-col'>
                  <KpiCard
                    title='Vendors'
                    value={failedSections.includes('Vendors') ? '—' : kpis.totalVendors}
                    sub={failedSections.includes('Vendors') ? 'Unavailable' : `${kpis.totalVendors} suppliers`}
                    onClick={() => openModal('vendors')}
                    meta={kpiMeta.totalVendors}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Modals ─── */}
      <KpiDetailModal open={activeModal === 'kpiDetail'} onClose={closeModal} kpis={kpis} />
      <CompaniesModal open={activeModal === 'companies'} onClose={closeModal} companies={data.companies} isFailed={failedSections.includes('Companies')} />
      <SubCompaniesModal open={activeModal === 'subCompanies'} onClose={closeModal} subCompanies={data.subCompanies} isFailed={failedSections.includes('Sub-Companies')} />
      <VesselsModal open={activeModal === 'vessels'} onClose={closeModal} vessels={data.vessels} isFailed={failedSections.includes('Vessels')} />
      <UsersModal open={activeModal === 'users'} onClose={closeModal} users={data.users} isFailed={failedSections.includes('Users')} />
      <VendorsModal open={activeModal === 'vendors'} onClose={closeModal} vendors={data.vendors} isFailed={failedSections.includes('Vendors')} />
      <RolesModal open={activeModal === 'roles'} onClose={closeModal} roles={data.roles} isFailed={failedSections.includes('Roles')} />
    </div>
  )
}

export default ManagementDashboard
